import React, { useState, useEffect } from 'react';

const DateRangePicker = ({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    defaultDays = 3,
    showQuickSelection = true,
    className = ""
}) => {
    const [localStartDate, setLocalStartDate] = useState(startDate);
    const [localEndDate, setLocalEndDate] = useState(endDate);

    // Format date for datetime-local input
    const formatForInput = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // Set default date range
    useEffect(() => {
        if (!startDate && !endDate) {
            const now = new Date();
            const defaultStartDate = new Date(now.getTime() - (defaultDays * 24 * 60 * 60 * 1000));

            const formattedStart = formatForInput(defaultStartDate);
            const formattedEnd = formatForInput(now);

            setLocalStartDate(formattedStart);
            setLocalEndDate(formattedEnd);

            onStartDateChange(formattedStart);
            onEndDateChange(formattedEnd);
        }
    }, [defaultDays, startDate, endDate, onStartDateChange, onEndDateChange]);

    // Update local state when props change
    useEffect(() => {
        if (startDate) setLocalStartDate(formatForInput(startDate));
        if (endDate) setLocalEndDate(formatForInput(endDate));
    }, [startDate, endDate]);

    // Function to set date range based on preset options
    const setDateRange = (days) => {
        const now = new Date();
        const startDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));

        const formattedStart = formatForInput(startDate);
        const formattedEnd = formatForInput(now);

        setLocalStartDate(formattedStart);
        setLocalEndDate(formattedEnd);

        onStartDateChange(formattedStart);
        onEndDateChange(formattedEnd);
    };

    const handleStartDateChange = (e) => {
        const value = e.target.value;
        setLocalStartDate(value);
        onStartDateChange(value);
    };

    const handleEndDateChange = (e) => {
        const value = e.target.value;
        setLocalEndDate(value);
        onEndDateChange(value);
    };

    const quickSelectionOptions = [
        { label: 'Last 1 Day', days: 1 },
        { label: 'Last 3 Days', days: 3 },
        { label: 'Last 1 Week', days: 7 },
        { label: 'Last 2 Weeks', days: 14 }
    ];

    return (
        <div className={className}>
            {showQuickSelection && (
                <div className="mb-3">
                    <label className="form-label">Quick Date Range Selection</label>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                        {quickSelectionOptions.map((option) => (
                            <button
                                key={option.days}
                                type="button"
                                className={`btn btn-sm ${option.days === defaultDays
                                        ? 'btn-outline-primary'
                                        : 'btn-outline-secondary'
                                    }`}
                                onClick={() => setDateRange(option.days)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="row">
                <div className="col-md-6 mb-3">
                    <label htmlFor="startDate" className="form-label">Start Date & Time</label>
                    <input
                        type="datetime-local"
                        className="form-control"
                        id="startDate"
                        value={localStartDate}
                        onChange={handleStartDateChange}
                        required
                    />
                </div>
                <div className="col-md-6 mb-3">
                    <label htmlFor="endDate" className="form-label">End Date & Time</label>
                    <input
                        type="datetime-local"
                        className="form-control"
                        id="endDate"
                        value={localEndDate}
                        onChange={handleEndDateChange}
                        required
                    />
                </div>
            </div>
        </div>
    );
};

export default DateRangePicker; 